<template>
  <b-overlay
    :show="isLoading"
    variant="transparent"
    opacity="0.99"
    blur="5px"
    rounded="sm"
  >
    <template #overlay>
      <div class="text-center">
        <b-icon-controller font-scale="3" animation="cylon" />
        <p id="cancel-label">
          กรุณารอสักครู่...
        </p>
      </div>
    </template>

    <div class="dashboard-wrapper">
      <!-- KPI วันนี้ -->
      <section class="dash-section">
        <header class="section-header">
          <div class="section-title">
            <span class="section-icon section-icon-primary">
              <i class="fas fa-tachometer-alt" />
            </span>
            <div>
              <h4 class="mb-0">ภาพรวมวันนี้</h4>
              <small class="text-muted">รายการสำคัญที่เกิดขึ้นในวันนี้</small>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary refresh-btn"
            :disabled="kpiLoading"
            @click="loadKpi"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': kpiLoading }" />
            รีเฟรช
          </button>
        </header>

        <div class="kpi-grid kpi-grid-top">
          <div
            v-for="card in kpiTopRow"
            :key="card.key"
            class="kpi-card"
            :class="`kpi-${card.color}`"
          >
            <div class="kpi-icon">
              <i :class="card.icon" />
            </div>
            <div class="kpi-body">
              <div class="kpi-meta">
                <span class="kpi-label">{{ card.label }}</span>
                <span v-if="card.badge" class="kpi-badge">
                  {{ formatInt(card.badge) }} {{ card.badgeUnit }}
                </span>
              </div>
              <div class="kpi-value">
                <span class="kpi-value-amount">{{ formatAmount(card.value) }}</span>
                <span class="kpi-value-unit">{{ card.unit }}</span>
              </div>
              <small v-if="card.hint" class="kpi-hint text-muted">{{ card.hint }}</small>
            </div>
          </div>
        </div>

        <div class="kpi-grid kpi-grid-bottom">
          <div
            v-for="card in kpiBottomRow"
            :key="card.key"
            class="kpi-card"
            :class="`kpi-${card.color}`"
          >
            <div class="kpi-icon">
              <i :class="card.icon" />
            </div>
            <div class="kpi-body">
              <div class="kpi-meta">
                <span class="kpi-label">{{ card.label }}</span>
                <span v-if="card.badge" class="kpi-badge">
                  {{ formatInt(card.badge) }} {{ card.badgeUnit }}
                </span>
              </div>
              <div class="kpi-value">
                <span class="kpi-value-amount">{{ formatAmount(card.value) }}</span>
                <span class="kpi-value-unit">{{ card.unit }}</span>
              </div>
              <small v-if="card.hint" class="kpi-hint text-muted">{{ card.hint }}</small>
            </div>
          </div>
        </div>
      </section>

      <!-- กำไรและสมาชิก -->
      <section class="dash-section">
        <header class="section-header">
          <div class="section-title">
            <span class="section-icon section-icon-success">
              <i class="fas fa-chart-line" />
            </span>
            <div>
              <h4 class="mb-0">กำไรและสมาชิก</h4>
              <small class="text-muted">กำไรสุทธิและพฤติกรรมผู้ใช้งาน</small>
            </div>
          </div>
        </header>

        <b-row match-height class="profit-users-row">
          <b-col lg="6" class="dashboard-equal-col">
            <SecProfit class="dashboard-equal-card" />
          </b-col>
          <b-col lg="6" class="dashboard-equal-col">
            <SecUsers class="dashboard-equal-card" />
          </b-col>
        </b-row>
      </section>

      <!-- อันดับสมาชิก -->
      <section class="dash-section">
        <header class="section-header">
          <div class="section-title">
            <span class="section-icon section-icon-info">
              <i class="fas fa-trophy" />
            </span>
            <div>
              <h4 class="mb-0">อันดับสมาชิก</h4>
              <small class="text-muted">สมาชิกที่ทำรายการสูงสุด</small>
            </div>
          </div>
        </header>

        <b-row>
          <b-col cols="12">
            <SecRanks />
          </b-col>
        </b-row>
      </section>

      <!-- สรุปรายงาน -->
      <section class="dash-section">
        <header class="section-header">
          <div class="section-title">
            <span class="section-icon section-icon-secondary">
              <i class="fas fa-file-chart-line" />
            </span>
            <div>
              <h4 class="mb-0">สรุปรายงาน</h4>
              <small class="text-muted">รายงานยอดฝาก/ถอนและกำไรสุทธิย้อนหลัง</small>
            </div>
          </div>
        </header>

        <b-row>
          <b-col lg="8" class="mb-1">
            <SecDailysum
              :is-loading="reportLoading"
              :report-day="reportDay"
              :profit-days="profitDays"
            />
          </b-col>
          <b-col lg="4" class="mb-1">
            <SecMonthlysum
              :is-loading="reportLoading"
              :report-year="reportYear"
            />
          </b-col>
        </b-row>
      </section>
    </div>
  </b-overlay>
</template>

<script>
import {
  BIconController, BRow, BCol, BOverlay,
} from 'bootstrap-vue'
import SecProfit from './sections/profit.vue'
import SecUsers from './sections/users.vue'
import SecRanks from './sections/ranks.vue'
import SecDailysum from './sections/dailysum.vue'
import SecMonthlysum from './sections/monthlysum.vue'

const KPI_DEFS = [
  {
    key: 'dep',
    type: 'TODAY_DEP',
    label: 'รายการฝากวันนี้',
    icon: 'fas fa-chevron-double-down',
    color: 'success',
    unit: 'THB',
    badgeUnit: 'รายการ',
    map: d => ({ value: d.todayDep || 0, badge: d.count || 0 }),
  },
  {
    key: 'wit',
    type: 'TODAY_WIT',
    label: 'รายการถอนวันนี้',
    icon: 'fas fa-chevron-double-up',
    color: 'danger',
    unit: 'THB',
    badgeUnit: 'รายการ',
    map: d => ({ value: d.todayWit || 0, badge: d.count || 0 }),
  },
  {
    key: 'cashback',
    type: 'TODAY_CASHBACK',
    label: 'คืนยอดเสีย',
    icon: 'fas fa-undo-alt',
    color: 'primary',
    unit: 'THB',
    map: d => ({ value: d.sum || 0 }),
  },
  {
    key: 'commission',
    type: 'TODAY_COMMISSION',
    label: 'ค่าคอมมิสชั่น',
    icon: 'fas fa-share-alt',
    color: 'teal',
    unit: 'THB',
    hint: 'Rolling',
    map: d => ({ value: d.sum || 0 }),
  },
  {
    key: 'compensation',
    type: 'AUTOPEER_TIMEOUT_COMP',
    label: 'ชดเชยเครดิต AutoPeer',
    icon: 'fas fa-plus-circle',
    color: 'orange',
    unit: 'THB',
    badgeUnit: 'รายการ',
    hidden: true, // ซ่อนไว้ เผื่อเปิดใช้อีก — เปลี่ยนเป็น false เพื่อแสดง
    map: d => ({ value: d.totalAmount || 0, badge: d.count || 0 }),
  },
  {
    key: 'first-dep',
    type: 'TODAY_FIRST_DEP',
    label: 'ยอดฝากแรกวันนี้',
    icon: 'fas fa-user-check',
    color: 'orange',
    unit: 'THB',
    badgeUnit: 'คน',
    map: d => ({ value: d.totalAmount || 0, badge: d.count || 0 }),
  },
  {
    key: 'bonus',
    type: 'TODAY_LUCKYWHEEL',
    label: 'รางวัลกงล้อ',
    icon: 'fas fa-gift',
    color: 'info',
    unit: 'THB',
    map: d => ({ value: d.todayBonus || 0 }),
  },
  {
    key: 'bonus-total',
    type: 'TODAY_BONUS_TOTAL',
    label: 'โบนัส',
    icon: 'fas fa-gift',
    color: 'purple',
    unit: 'THB',
    badgeUnit: 'รายการ',
    map: d => ({ value: d.sum || 0, badge: d.count || 0 }),
  },
  {
    key: 'lotto-any',
    type: 'TODAY_LOTTO_ANY',
    label: 'ซื้อด้วยเพชร & กงล้อ',
    icon: 'fas fa-gem',
    color: 'pink',
    unit: 'ใบ',
    badgeUnit: 'หมุน',
    map: d => ({ value: d.chip || 0, badge: d.wheel || 0 }),
  },
]

export default {
  components: {
    BIconController,
    BRow,
    BCol,
    BOverlay,
    SecProfit,
    SecUsers,
    SecRanks,
    SecDailysum,
    SecMonthlysum,
  },
  data() {
    return {
      isLoading: false,
      reportDay: [],
      reportYear: [],
      profitDays: '',
      profitDays_kr: '',
      reportLoading: false,
      kpiLoading: false,
      kpiData: {},
    }
  },
  computed: {
    visibleKpiDefs() {
      return KPI_DEFS.filter(def => !def.hidden)
    },
    kpiCards() {
      return this.visibleKpiDefs.map(def => {
        const raw = this.kpiData[def.key]
        const mapped = raw ? def.map(raw) : { value: 0, badge: 0 }
        return {
          ...def,
          value: mapped.value,
          badge: mapped.badge,
        }
      })
    },
    kpiTopRow() {
      return this.kpiCards.slice(0, 4)
    },
    kpiBottomRow() {
      return this.kpiCards.slice(4)
    },
  },
  mounted() {
    this.getReportDay()
    this.loadKpi()
  },
  methods: {
    formatAmount(value) {
      const num = Number(value || 0)
      if (Number.isInteger(num)) {
        return num.toLocaleString('en-US')
      }
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    formatInt(value) {
      return Number(value || 0).toLocaleString('en-US')
    },
    async loadKpi() {
      if (this.kpiLoading) return
      this.kpiLoading = true
      try {
        const defs = this.visibleKpiDefs
        const results = await Promise.allSettled(
          defs.map(def => this.$http.get(`/dashboard?type=${def.type}`))
        )
        const next = {}
        results.forEach((res, idx) => {
          const def = defs[idx]
          next[def.key] = res.status === 'fulfilled' ? res.value.data : null
        })
        this.kpiData = next
      } catch (error) {
        console.log('[dashboard] loadKpi error', error)
      } finally {
        this.kpiLoading = false
      }
    },
    getReportDay() {
      this.reportLoading = true
      this.$http
        .get('dashboard/ReportDaysAndYears')
        .then(response => {
          this.reportDay = response.data.days
          this.profitDays = response.data.profitDay
          this.profitDays_kr = response.data.profitDay_kr
          this.reportYear = response.data.years
        })
        .catch(error => console.log(error))
        .finally(() => {
          this.reportLoading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-wrapper {
  padding: 4px 0 24px;
}

.dash-section {
  margin-bottom: 22px;
}

.dashboard-equal-col {
  display: flex;
}

.dashboard-equal-card {
  width: 100%;
  height: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 0 4px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title h4 {
  font-weight: 700;
  font-size: 1.05rem;
  color: #283046;
}

.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  font-size: 1.05rem;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.section-icon-primary { background: linear-gradient(135deg, #7367f0, #9e95f5); }
.section-icon-success { background: linear-gradient(135deg, #28c76f, #48da89); }
.section-icon-info    { background: linear-gradient(135deg, #00cfe8, #1ad7ee); }
.section-icon-warning { background: linear-gradient(135deg, #ff9f43, #ffb976); }
.section-icon-secondary { background: linear-gradient(135deg, #82868b, #a4a8ad); }

.refresh-btn {
  border-radius: 10px;
  font-size: 0.78rem;
  padding: 5px 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* KPI grid: 4 + 4 layout */
.kpi-grid {
  display: grid;
  --kpi-gap: 14px;
  gap: var(--kpi-gap);
}

.kpi-grid-top    { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: var(--kpi-gap); }
.kpi-grid-bottom { grid-template-columns: repeat(4, minmax(0, 1fr)); }

@media (max-width: 1199.98px) {
  .kpi-grid-top,
  .kpi-grid-bottom { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 767.98px) {
  .kpi-grid-top,
  .kpi-grid-bottom { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 479.98px) {
  .kpi-grid-top,
  .kpi-grid-bottom { grid-template-columns: 1fr; }
}

/* KPI card */
.kpi-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(34, 41, 47, 0.06);
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.04);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  min-height: 100px;
}

.kpi-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: var(--kpi-gradient, linear-gradient(135deg, rgba(115, 103, 240, 0.07), rgba(115, 103, 240, 0)));
  opacity: 0.9;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(34, 41, 47, 0.08);
}

.kpi-icon {
  position: relative;
  z-index: 1;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: #fff;
  flex-shrink: 0;
  background: var(--kpi-color, #7367f0);
  box-shadow: 0 6px 14px var(--kpi-shadow, rgba(115, 103, 240, 0.3));
}

.kpi-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.kpi-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 2px;
}

.kpi-label {
  font-size: 0.9rem;
  color: #6e6b7b;
  font-weight: 600;
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.12);
  color: #5e50ee;
  white-space: nowrap;
}

.kpi-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 2px;
}

.kpi-value-amount {
  font-weight: 700;
  font-size: 1.34rem;
  color: #1f2a44;
  line-height: 1.2;
  word-break: break-all;
}

.kpi-value-unit {
  font-size: 0.8rem;
  color: #98a0b3;
  font-weight: 600;
}

.kpi-hint {
  font-size: 0.78rem;
  margin-top: 2px;
}

/* Color variants */
.kpi-success  { --kpi-color: linear-gradient(135deg, #28c76f, #48da89); --kpi-shadow: rgba(40, 199, 111, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(40, 199, 111, 0.08), rgba(40, 199, 111, 0)); }
.kpi-success .kpi-badge { background: rgba(40, 199, 111, 0.12); color: #1d995a; }

.kpi-danger   { --kpi-color: linear-gradient(135deg, #ea5455, #ff7b7c); --kpi-shadow: rgba(234, 84, 85, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(234, 84, 85, 0.08), rgba(234, 84, 85, 0)); }
.kpi-danger .kpi-badge { background: rgba(234, 84, 85, 0.12); color: #d23a3b; }

.kpi-info     { --kpi-color: linear-gradient(135deg, #00cfe8, #1ad7ee); --kpi-shadow: rgba(0, 207, 232, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(0, 207, 232, 0.08), rgba(0, 207, 232, 0)); }
.kpi-info .kpi-badge { background: rgba(0, 207, 232, 0.12); color: #009fb5; }

.kpi-warning  { --kpi-color: linear-gradient(135deg, #ff9f43, #ffb976); --kpi-shadow: rgba(255, 159, 67, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(255, 159, 67, 0.08), rgba(255, 159, 67, 0)); }
.kpi-warning .kpi-badge { background: rgba(255, 159, 67, 0.14); color: #b66a18; }

.kpi-primary  { --kpi-color: linear-gradient(135deg, #7367f0, #9e95f5); --kpi-shadow: rgba(115, 103, 240, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(115, 103, 240, 0.08), rgba(115, 103, 240, 0)); }

.kpi-orange   { --kpi-color: linear-gradient(135deg, #ff6b35, #ff8f5e); --kpi-shadow: rgba(255, 107, 53, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(255, 107, 53, 0.08), rgba(255, 107, 53, 0)); }
.kpi-orange .kpi-badge { background: rgba(255, 107, 53, 0.14); color: #d54e1d; }

.kpi-teal     { --kpi-color: linear-gradient(135deg, #16a085, #1abc9c); --kpi-shadow: rgba(22, 160, 133, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(22, 160, 133, 0.08), rgba(22, 160, 133, 0)); }

.kpi-purple   { --kpi-color: linear-gradient(135deg, #8e44ad, #b365d3); --kpi-shadow: rgba(142, 68, 173, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(142, 68, 173, 0.08), rgba(142, 68, 173, 0)); }
.kpi-purple .kpi-badge { background: rgba(142, 68, 173, 0.14); color: #6f3393; }

.kpi-pink     { --kpi-color: linear-gradient(135deg, #ec407a, #f48fb1); --kpi-shadow: rgba(236, 64, 122, 0.28); --kpi-gradient: linear-gradient(135deg, rgba(236, 64, 122, 0.08), rgba(236, 64, 122, 0)); }
.kpi-pink .kpi-badge { background: rgba(236, 64, 122, 0.14); color: #c93060; }

/* Outer card polish for child sections (profit/users/ranks/reports) */
::v-deep .card {
  border-radius: 14px !important;
  border: 1px solid rgba(34, 41, 47, 0.06);
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.04);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;
}

::v-deep .card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(34, 41, 47, 0.08);
}

::v-deep .card-header {
  padding: 1rem 1.25rem 0.5rem;
}

::v-deep .card-title {
  font-weight: 700;
  letter-spacing: 0.2px;
}

::v-deep .text-hede {
  font-weight: 600;
  letter-spacing: 0.2px;
  font-size: 0.92rem;
  color: #5e6478;
}

.btn-success {
  box-shadow: rgba(0, 204, 136, 0.25) 0px 0px 10px 2px !important;
}

.btn-danger {
  box-shadow: rgba(230, 63, 89, 0.25) 0px 0px 10px 2px !important;
}
</style>
